import React, { ReactNode } from 'react';
import { gql, useQuery } from '@apollo/client';
import { WrappedSpinner } from 'spinner';
import env from 'bench-environment';
import { NullState } from 'bench-internal-theme';

const GET_CURRENT_USER = gql`
  query getCurrentUser {
    currentUser {
      id
      fullName
    }
  }
`;

const GET_LOGGED_IN_PERSON = gql`
  query getLoggedInPerson {
    loggedInPerson {
      id
      fullName
      globalRoles {
        id
        code
      }
    }
  }
`;

type GlobalRole = {
  id: string;
  code: GlobalRoleCode;
};

export enum GlobalRoleCode {
  SUPERUSER = 'SUPERUSER',
  SPECIALIST = 'SPECIALIST',
  USER = 'USER',
}

const AuthWrapper = ({
  children,
  useLoggedInPersonQuery = false,
  authorizedGlobalRoles,
}: {
  children: ReactNode;
  useLoggedInPersonQuery?: boolean;
  authorizedGlobalRoles?: GlobalRoleCode[];
}) => {
  const { data, loading, error } = useQuery(
    useLoggedInPersonQuery ? GET_LOGGED_IN_PERSON : GET_CURRENT_USER,
  );

  if (loading) {
    return <WrappedSpinner></WrappedSpinner>;
  }

  if ((!data?.currentUser?.id && !data?.loggedInPerson?.id) || error) {
    window.location.href = `${env.location.websiteRoot}/logout`;
    return <></>;
  }

  const personGlobalRoles: GlobalRole[] = data?.loggedInPerson?.globalRoles;

  if (authorizedGlobalRoles?.length) {
    const isUnauthorizedPerson = !personGlobalRoles?.some(({ code }) =>
      authorizedGlobalRoles.includes(code),
    );
    if (isUnauthorizedPerson) {
      return (
        <NullState
          bigEmoji="🔮"
          description="Contact support to gain access."
          title="Looks like you don't quite have the permission for this page."
        />
      );
    }
  }

  return <>{children}</>;
};

export default AuthWrapper;
